import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle, Eye, EyeOff, Download, ChevronRight } from 'lucide-react';
type TabType = 'pending' | 'ready' | 'approved' | 'paid' | 'history';
const payouts = [{
  member: 'Mark Johnson',
  role: 'Lead Vocals/Guitar',
  model: 'Equal Split (25%)',
  amount: '$587.50',
  visibility: 'Full',
  status: 'Ready'
}, {
  member: 'Sarah Williams',
  role: 'Violin',
  model: 'Equal Split (25%)',
  amount: '$587.50',
  visibility: 'Restricted',
  status: 'Ready'
}, {
  member: 'David Chen',
  role: 'Bass',
  model: 'Equal Split (25%)',
  amount: '$587.50',
  visibility: 'Restricted',
  status: 'Ready'
}, {
  member: 'Emma Davis',
  role: 'Drums',
  model: 'Equal Split (25%)',
  amount: '$587.50',
  visibility: 'Restricted',
  status: 'Ready'
}];
export function MemberPayoutsPage() {
  const [activeTab, setActiveTab] = useState<TabType>('ready');
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="max-w-7xl mx-auto" data-id="element-4016">
      <div className="mb-8" data-id="element-4017">
        <h2 className="text-2xl font-bold mb-1" data-id="element-4018">Member Payouts</h2>
        <p className="text-sm text-textMuted" data-id="element-4019">
          Calculate and approve payouts for band members and crew.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-6 border-b border-border mb-6 overflow-x-auto" data-id="element-4020">
        {(['pending', 'ready', 'approved', 'paid', 'history'] as TabType[]).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-3 text-sm font-medium transition-colors whitespace-nowrap capitalize ${activeTab === tab ? 'text-primary border-b-2 border-primary' : 'text-textMuted hover:text-textMain'}`} data-id="element-4021">
              {tab === 'ready' ? 'Ready for Approval' : tab === 'pending' ? 'Pending Calculation' : tab}
            </button>)}
      </div>

      {/* Show Batch Header */}
      <div className="bg-surface border border-border rounded-xl p-6 mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-4022">
        <div data-id="element-4023">
          <div className="flex items-center gap-2 text-sm text-textMuted mb-1" data-id="element-4024">
            <span data-id="element-4025">The Sinclair</span>
            <ChevronRight className="w-3 h-3" data-id="element-4026" />
            <span data-id="element-4027">May 20, 2026</span>
          </div>
          <h3 className="text-xl font-bold" data-id="element-4028">Payout Batch: $2,350.00</h3>
        </div>
        <div className="flex items-center gap-3" data-id="element-4029">
          <button className="px-4 py-2 bg-surface border border-border rounded-lg text-sm font-medium hover:bg-surfaceHover transition-colors flex items-center gap-2" data-id="element-4030">
            <Download className="w-4 h-4" data-id="element-4031" /> Export CSV
          </button>
          <button className="px-4 py-2 bg-primary text-background rounded-lg text-sm font-medium hover:bg-primaryHover transition-colors flex items-center gap-2" data-id="element-4032">
            <CheckCircle2 className="w-4 h-4" data-id="element-4033" /> Approve All
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface border border-border rounded-xl overflow-hidden" data-id="element-4034">
        <table className="w-full text-sm text-left" data-id="element-4035">
          <thead className="text-xs text-textMuted uppercase bg-background/50 border-b border-border" data-id="element-4036">
            <tr data-id="element-4037">
              <th className="px-6 py-4 font-medium" data-id="element-4038">Member</th>
              <th className="px-6 py-4 font-medium" data-id="element-4039">Role</th>
              <th className="px-6 py-4 font-medium" data-id="element-4040">Payout Model</th>
              <th className="px-6 py-4 font-medium" data-id="element-4041">Amount Due</th>
              <th className="px-6 py-4 font-medium" data-id="element-4042">Visibility</th>
              <th className="px-6 py-4 font-medium" data-id="element-4043">Status</th>
              <th className="px-6 py-4 font-medium text-right" data-id="element-4044">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border" data-id="element-4045">
            {payouts.map((payout, i) => <tr key={i} className="hover:bg-surfaceHover/50 transition-colors" data-id="element-4046">
                <td className="px-6 py-4 font-medium" data-id="element-4047">{payout.member}</td>
                <td className="px-6 py-4 text-textMuted" data-id="element-4048">{payout.role}</td>
                <td className="px-6 py-4 text-textMuted" data-id="element-4049">{payout.model}</td>
                <td className="px-6 py-4 font-bold text-green-500" data-id="element-4050">
                  {payout.amount}
                </td>
                <td className="px-6 py-4" data-id="element-4051">
                  <div className="flex items-center gap-1.5 text-xs text-textMuted" data-id="element-4052">
                    {payout.visibility === 'Full' ? <Eye className="w-3 h-3" data-id="element-4053" /> : <EyeOff className="w-3 h-3" data-id="element-4054" />}
                    {payout.visibility}
                  </div>
                </td>
                <td className="px-6 py-4" data-id="element-4055">
                  <span className="px-2 py-1 bg-primary/10 text-primary text-[10px] font-bold uppercase tracking-wider rounded" data-id="element-4056">
                    {payout.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-right" data-id="element-4057">
                  <button className="text-primary hover:underline text-xs font-medium" data-id="element-4058">
                    Review
                  </button>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </motion.div>;
}